import { GROQ_MODEL } from "@/config/api";

interface Message {
  role: "system" | "user" | "assistant";
  content: string;
}

interface GroqResponse {
  choices: {
    message: {
      role: string;
      content: string;
    };
    index: number;
    finish_reason: string;
  }[];
}

interface ErrorResponse {
  error: string;
  details?: string;
  suggestion?: string;
  retryAfter?: number;
  debug?: {
    envKeys?: string[];
    cwd?: string;
    nodeEnv?: string;
    netlifyDev?: string;
    apiKeyLength?: number;
  };
}

async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Helper function to get the base URL for API calls
function getBaseUrl() {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8888';
  }
  return ''; // Empty string for production (relative URL)
}

export async function askGroq(
  messages: Message[],
  temperature = 0.7,
  maxRetries = 3
): Promise<string> {
  let retries = 0;
  const baseUrl = getBaseUrl();
  
  while (retries < maxRetries) {
    try {
      console.log('Making request to Groq proxy...');
      const response = await fetch(`${baseUrl}/.netlify/functions/groq-proxy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: GROQ_MODEL,
          messages,
          temperature,
        }),
      });

      let data;
      try {
        const text = await response.text();
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          console.error('Raw response:', text);
          throw new Error(`Failed to parse response: ${parseError.message}. Raw response: ${text}`);
        }
      } catch (textError) {
        console.error('Error reading response:', textError);
        throw new Error(`Failed to read response: ${textError.message}`);
      }

      if (response.status === 429) {
        const waitTime = data.retryAfter ? data.retryAfter * 1000 : Math.pow(2, retries) * 1000;
        console.log(`Rate limit hit, waiting ${waitTime}ms before retry...`);
        await sleep(waitTime);
        retries++;
        continue;
      }

      if (!response.ok) {
        const errorData = data as ErrorResponse;
        console.error('Groq API error:', errorData);
        
        // Check for specific error conditions
        if (errorData.error === "Groq API key not configured") {
          throw new Error("API configuration error: Please check server configuration");
        }
        
        if (errorData.error === "Invalid JSON") {
          throw new Error(`Invalid request format: ${errorData.details}`);
        }
        
        throw new Error(errorData.details || errorData.error || `Groq API error: ${response.status}`);
      }

      const groqData = data as GroqResponse;
      if (!groqData.choices?.[0]?.message?.content) {
        throw new Error("Invalid response format from Groq API");
      }
      
      return groqData.choices[0].message.content;
    } catch (error) {
      console.error("Error calling Groq API:", error);
      if (retries === maxRetries - 1) {
        throw error; // Let the caller handle the error
      }
      retries++;
      await sleep(Math.pow(2, retries) * 1000);
    }
  }
  
  throw new Error("Max retries reached. Please try again later.");
}

// Generate itinerary using Groq
export async function generateItinerary(
  destination: string,
  days: number,
  interests: string[],
  budget?: string
): Promise<string> {
  const systemPrompt = `You are an expert travel planner. Create a detailed day-by-day itinerary for the requested destination.
  Format your response in a clean, readable markdown format with day headers, attractions, meal recommendations, and approximate timings.
  Include local tips and cultural insights where relevant.`;
  
  const userPrompt = `Please create a ${days}-day itinerary for ${destination}. 
  My interests include: ${interests.join(", ")}. ${budget ? `My budget is approximately ${budget}.` : ""}
  Include recommendations for attractions, activities, restaurants, and transportation between locations.`;
  
  return askGroq([
    { role: "system", content: systemPrompt },
    { role: "user", content: userPrompt }
  ], 0.7);
}
